import { toast } from 'sonner';

const API_BASE = (import.meta.env?.VITE_API_BASE as string | undefined) || '/api';
const TOKEN_KEY = 'auth_token';

export type RequestOptions = RequestInit & {
	params?: Record<string, string | number | boolean | undefined | null>;
};

export type ApiRecord = Record<string, unknown>;

type ListEnvelope = {
	data?: ApiRecord[];
	companies?: ApiRecord[];
	contacts?: ApiRecord[];
	opportunities?: ApiRecord[];
	activities?: ApiRecord[];
	emails?: ApiRecord[];
	users?: ApiRecord[];
	mailboxes?: ApiRecord[];
	profiles?: ApiRecord[];
	domains?: ApiRecord[];
	vendors?: ApiRecord[];
	products?: ApiRecord[];
	providers?: ApiRecord[];
	phone_numbers?: ApiRecord[];
	calls?: ApiRecord[];
	results?: ApiRecord[];
	total?: number;
	page?: number;
	limit?: number;
};

export type AuthUser = {
	id?: string | number;
	email?: string;
	name?: string;
	role?: string;
};

type AuthMeEnvelope = {
	user?: AuthUser;
	is_setup_needed?: boolean;
};

export type PBXPublicConfig = {
	enabled: boolean;
	wss_url?: string;
	sip_domain?: string;
	stun_server?: string;
};

export type PublicConfig = {
	baseUrl?: string;
	needsSetup?: boolean;
	oauthProviders?: Array<{ slug: string; name: string }>;
	pbx?: PBXPublicConfig;
};

function getStoredToken() {
	return window.localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string | null) {
	if (token) {
		window.localStorage.setItem(TOKEN_KEY, token);
		return;
	}
	window.localStorage.removeItem(TOKEN_KEY);
}

function buildUrl(path: string, params?: RequestOptions['params']) {
	const url = new URL(`${API_BASE}${path}`);
	if (params) {
		Object.entries(params).forEach(([key, value]) => {
			if (value !== undefined && value !== null && value !== '') {
				url.searchParams.set(key, String(value));
			}
		});
	}
	return url.toString();
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
	const token = getStoredToken();
	const headers = new Headers(options.headers);
	if (!headers.has('Content-Type') && options.body) {
		headers.set('Content-Type', 'application/json');
	}
	if (token) {
		headers.set('Authorization', `Bearer ${token}`);
	}

	const response = await fetch(buildUrl(path, options.params), {
		...options,
		headers,
		credentials: 'include'
	});

	if (response.status === 401) {
		setStoredToken(null);
		toast.error('Your session has expired. Please sign in again.');
		throw new Error('Unauthorized');
	}

	if (!response.ok) {
		const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
		throw new Error(error.message || `HTTP ${response.status}`);
	}

	if (response.status === 204) {
		return null as T;
	}

	const contentType = response.headers.get('content-type') || '';
	if (contentType.includes('application/json')) {
		return response.json() as Promise<T>;
	}

	return null as T;
}

function getListFromEnvelope(response: ApiRecord[] | ListEnvelope): ApiRecord[] {
	if (Array.isArray(response)) {
		return response;
	}

	return (
		response.data ??
		response.companies ??
		response.contacts ??
		response.opportunities ??
		response.activities ??
		response.emails ??
		response.users ??
		response.mailboxes ??
		response.profiles ??
		response.domains ??
		response.vendors ??
		response.products ??
		response.providers ??
		response.phone_numbers ??
		response.calls ??
		response.results ??
		[]
	);
}

export const api = {
	get: <T>(path: string, params?: RequestOptions['params']) => request<T>(path, { method: 'GET', params }),
	post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : null }),
	put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : null }),
	patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : null }),
	delete: <T>(path: string) => request<T>(path, { method: 'DELETE' })
};

export const authApi = {
	checkSetup: () => api.get<{ needsSetup: boolean }>('/auth/check-setup'),
	me: async () => {
		const response = await api.get<AuthUser | AuthMeEnvelope>('/auth/me');
		if ('user' in response && response.user) {
			return response.user;
		}
		return response as AuthUser;
	},
	login: (email: string, password: string) => api.post<{ token?: string; user?: AuthUser }>('/auth/login', { email, password }),
	setupAdmin: (payload: { email: string; password: string; firstName: string; lastName: string }) =>
		api.post<{ token?: string; user?: AuthUser }>('/auth/setup-admin', payload),
	logout: () => api.post<void>('/auth/logout'),
	oidcStart: (provider: string) => api.get<{ auth_url?: string; authURL?: string }>(`/auth/${provider}`),
	publicConfig: async (): Promise<PublicConfig> => {
		const response = await api.get<{
			base_url?: string;
			setup_needed?: boolean;
			oauth_providers?: Array<{ slug: string; name: string }>;
			baseUrl?: string;
			needsSetup?: boolean;
			oauthProviders?: Array<{ slug: string; name: string }>;
		}>('/config/public');

		return {
			baseUrl: response.baseUrl ?? response.base_url,
			needsSetup: response.needsSetup ?? response.setup_needed,
			oauthProviders: response.oauthProviders ?? response.oauth_providers ?? [],
			pbx: (response as Record<string, unknown>).pbx as PBXPublicConfig | undefined
		};
	}
};

export const crmApi = {
	companies: async (params?: RequestOptions['params']) => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/companies', params)),
	createCompany: (payload: Record<string, unknown>) => api.post<ApiRecord>('/companies', payload),
	deleteCompany: (id: number | string) => api.delete<void>(`/companies/${id}`),
	company: (id: number | string) => api.get<ApiRecord>(`/companies/${id}`),
	contacts: async (params?: RequestOptions['params']) => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/contacts', params)),
	createContact: (payload: Record<string, unknown>) => api.post<ApiRecord>('/contacts', payload),
	updateContact: (id: number | string, payload: Record<string, unknown>) => api.patch<ApiRecord>(`/contacts/${id}`, payload),
	deleteContact: (id: number | string) => api.delete<void>(`/contacts/${id}`),
	contact: (id: number | string) => api.get<ApiRecord>(`/contacts/${id}`),
	contactLeadStatuses: async () => api.get<Array<{ value: string; label: string }>>('/contacts/lead-statuses'),
	contactFollowUps: async (params?: RequestOptions['params']) => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/contacts/follow-ups', params)),
	opportunities: async (params?: RequestOptions['params']) => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/opportunities', params)),
	createOpportunity: (payload: Record<string, unknown>) => api.post<ApiRecord>('/opportunities', payload),
	deleteOpportunity: (id: number | string) => api.delete<void>(`/opportunities/${id}`),
	opportunity: (id: number | string) => api.get<ApiRecord>(`/opportunities/${id}`),
	opportunityActivities: async (id: number | string, params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>(`/opportunities/${id}/activities`, params)),
	createOpportunityActivity: (id: number | string, payload: Record<string, unknown>) => api.post<ApiRecord>(`/opportunities/${id}/activities`, payload),
	emails: async (params?: RequestOptions['params']) => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/emails', params)),
	email: (id: number | string) => api.get<ApiRecord>(`/emails/${id}`),
	contactEmails: async (contactId: number | string, params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>(`/contacts/${contactId}/emails`, params)),
	opportunityEmails: async (opportunityId: number | string, params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>(`/opportunities/${opportunityId}/emails`, params)),
	sendEmail: (payload: Record<string, unknown>) => api.post<ApiRecord>('/email/send', payload),
	emailMailboxes: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/email/mailboxes')),
	activities: async (params?: RequestOptions['params']) => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/activities', params)),
	contactCalls: async (contactId: number | string, params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>(`/contacts/${contactId}/calls`, params)),
	companyCalls: async (companyId: number | string, params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>(`/companies/${companyId}/calls`, params)),
	search: (query: string) => api.get<ApiRecord[]>('/search', { q: query }),
	adminStats: () => api.get<Record<string, unknown>>('/admin/stats'),
	businessUnits: async () => {
		const response = await api.get<{ business_units?: ApiRecord[] }>('/business-units');
		return response.business_units ?? [];
	},
	businessUnitPipeline: (id: number | string) =>
		api.get<{ columns: Array<{ stage: ApiRecord; opportunities: ApiRecord[]; total: number; amount_total: number }>; stages: ApiRecord[] }>(`/business-units/${id}/pipeline`),
	businessUnitStages: (id: number | string) =>
		api.get<{ stages: ApiRecord[]; uses_defaults: boolean }>(`/business-units/${id}/stages`),
	createBusinessUnitStage: (id: number | string, payload: Record<string, unknown>) =>
		api.post<ApiRecord>(`/business-units/${id}/stages`, payload),
	updatePipelineStage: (id: number | string, payload: Record<string, unknown>) =>
		api.patch<ApiRecord>(`/pipeline-stages/${id}`, payload),
	deletePipelineStage: (id: number | string) => api.delete<void>(`/pipeline-stages/${id}`),
	marketingCampaigns: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/marketing/campaigns')),
	leadIntake: async (params?: RequestOptions['params']) => {
		const response = await api.get<{ intakes?: ApiRecord[]; total?: number }>('/leads/intake', params);
		return { intakes: response.intakes ?? [], total: response.total ?? 0 };
	},
	leadIntakeGet: (id: number | string) => api.get<ApiRecord>(`/leads/intake/${id}`),
	leadIntakeSubmit: (payload: Record<string, unknown> | Record<string, unknown>[]) =>
		api.post<{ intakes?: ApiRecord[]; accepted?: number }>('/leads/intake', payload),
	leadIntakeApprove: (id: number | string, payload: Record<string, unknown>) =>
		api.post<{ intake_id: number; contact_id: number; company_id?: number | null }>(`/leads/intake/${id}/approve`, payload),
	leadIntakeReject: (id: number | string, reviewNote?: string) =>
		api.post<{ ok: boolean }>(`/leads/intake/${id}/reject`, reviewNote ? { review_note: reviewNote } : {}),
	leadIntakeMerge: (id: number | string, contactId: number, reviewNote?: string) =>
		api.post<{ ok: boolean; merged_into: number }>(`/leads/intake/${id}/merge`, { contact_id: contactId, review_note: reviewNote }),
	leadEnhance: (id: number | string) =>
		api.post<{ configured: boolean; message: string; contact_id: number }>(`/leads/${id}/enhance`, {}),
	logCall: (payload: {
		contact_id: number;
		to_number?: string;
		disposition: string;
		notes?: string | null;
		duration_seconds?: number;
		status?: string;
		lead_status?: string | null;
		follow_up_date?: string | null;
		follow_up_note?: string | null;
	}) => api.post<{ call: ApiRecord; contact_id: number }>('/calls/log', payload),
	marketingTemplates: async () =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/marketing/templates')),
	marketingPreviewAudience: (filters: Record<string, unknown>) =>
		api.post<{ count: number }>('/marketing/audience/count', filters),
	marketingQueryAudience: (query: Record<string, unknown>) =>
		api.post<{ contacts: ApiRecord[]; total: number }>('/marketing/audience/query', query),
	marketingCreateCampaign: (payload: Record<string, unknown>) =>
		api.post<ApiRecord>('/marketing/campaigns', payload),
	marketingAddAudienceToCampaign: (id: number | string, filters: Record<string, unknown>) =>
		api.post<{ added: number; total_recipients: number }>(`/marketing/campaigns/${id}/audience`, filters),
	marketingSendCampaign: (id: number | string) =>
		api.post<ApiRecord>(`/marketing/campaigns/${id}/send`, {}),
	marketingScheduleCampaign: (id: number | string, scheduledAt: string) =>
		api.post<ApiRecord>(`/marketing/campaigns/${id}/schedule`, { scheduled_at: scheduledAt }),
	inboxUnified: (params?: RequestOptions['params']) =>
		api.get<{ items: ApiRecord[] }>('/inbox/unified', params),
	routingRules: async () => {
		const r = await api.get<{ rules: ApiRecord[] }>('/routing-rules');
		return r.rules ?? [];
	},
	createRoutingRule: (payload: Record<string, unknown>) => api.post<ApiRecord>('/routing-rules', payload),
	deleteRoutingRule: (id: number | string) => api.delete<void>(`/routing-rules/${id}`),
	importLeads: async (file: File): Promise<{ imported: number; skipped: number; errors: number; results: ApiRecord[] }> => {
		const token = getStoredToken();
		const formData = new FormData();
		formData.append('file', file);
		const headers = new Headers();
		if (token) {
			headers.set('Authorization', `Bearer ${token}`);
		}
		const response = await fetch(buildUrl('/import/leads'), {
			method: 'POST',
			headers,
			credentials: 'include',
			body: formData
		});
		if (response.status === 401) {
			setStoredToken(null);
			toast.error('Your session has expired. Please sign in again.');
			throw new Error('Unauthorized');
		}
		if (!response.ok) {
			const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
			throw new Error(error.message || `HTTP ${response.status}`);
		}
		return response.json();
	}
};

export const adminApi = {
	users: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/users')),
	createUser: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/users', payload),
	updateUser: (id: number | string, payload: Record<string, unknown>) => api.put<ApiRecord>(`/admin/users/${id}`, payload),
	deleteUser: (id: number | string) => api.delete<void>(`/admin/users/${id}`),
	mailboxes: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/mailboxes')),
	createMailbox: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/mailboxes', payload),
	deleteMailbox: (id: number | string) => api.delete<void>(`/admin/mailboxes/${id}`),
	domains: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/domains')),
	createDomain: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/domains', payload),
	deleteDomain: (id: number | string) => api.delete<void>(`/admin/domains/${id}`),
	smtpProfiles: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/smtp-profiles')),
	createSMTPProfile: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/smtp-profiles', payload),
	deleteSMTPProfile: (id: number | string) => api.delete<void>(`/admin/smtp-profiles/${id}`),
	vendors: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/vendors')),
	createVendor: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/vendors', payload),
	deleteVendor: (id: number | string) => api.delete<void>(`/admin/vendors/${id}`),
	products: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/products')),
	createProduct: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/products', payload),
	deleteProduct: (id: number | string) => api.delete<void>(`/admin/products/${id}`),
	oauthProviders: async () => getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/admin/oauth/providers')),
	createOAuthProvider: (payload: Record<string, unknown>) => api.post<ApiRecord>('/admin/oauth/providers', payload),
	deleteOAuthProvider: (id: number | string) => api.delete<void>(`/admin/oauth/providers/${id}`)
};

export const profileApi = {
	getProfile: () => api.get<ApiRecord>('/profile'),
	updateProfile: (payload: Record<string, unknown>) => api.patch<ApiRecord>('/profile', payload),
	uploadAvatar: async (file: File): Promise<ApiRecord> => {
		const token = getStoredToken();
		const formData = new FormData();
		formData.append('avatar', file);
		const headers = new Headers();
		if (token) headers.set('Authorization', `Bearer ${token}`);
		const response = await fetch(buildUrl('/profile/avatar'), {
			method: 'POST',
			headers,
			credentials: 'include',
			body: formData
		});
		if (response.status === 401) {
			setStoredToken(null);
			toast.error('Your session has expired. Please sign in again.');
			throw new Error('Unauthorized');
		}
		if (!response.ok) {
			const error = await response.json().catch(() => ({ message: `HTTP ${response.status}` }));
			throw new Error((error as { message?: string }).message || `HTTP ${response.status}`);
		}
		return response.json() as Promise<ApiRecord>;
	}
};

export const phoneApi = {
	// Phone numbers
	phoneNumbers: async (params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/phone-numbers', params)),
	createPhoneNumber: (payload: Record<string, unknown>) => api.post<ApiRecord>('/phone-numbers', payload),
	updatePhoneNumber: (id: number | string, payload: Record<string, unknown>) => api.put<ApiRecord>(`/phone-numbers/${id}`, payload),
	deletePhoneNumber: (id: number | string) => api.delete<void>(`/phone-numbers/${id}`),

	// Caller lookup
	lookupCaller: async (number: string) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/phone/lookup', { number })),

	// Calls
	calls: async (params?: RequestOptions['params']) =>
		getListFromEnvelope(await api.get<ApiRecord[] | ListEnvelope>('/calls', params)),
	call: (id: number | string) => api.get<ApiRecord>(`/calls/${id}`),
	createCall: (payload: Record<string, unknown>) => api.post<ApiRecord>('/calls', payload),
	updateCall: (id: number | string, payload: Record<string, unknown>) => api.put<ApiRecord>(`/calls/${id}`, payload),

	// Originate (click-to-call)
	originate: (payload: { to_number: string; contact_id?: number; company_id?: number }) =>
		api.post<{ channel_id: string; call: ApiRecord }>('/phone/originate', payload),

	// PBX health
	health: () => api.get<{ enabled: boolean; healthy?: boolean }>('/phone/health')
};

export const mapApi = {
	// Get companies for map with optional filters
	companies: async (params?: RequestOptions['params']) => {
		const response = await api.get<{ companies: ApiRecord[]; total: number }>('/map/companies', params);
		return response.companies ?? [];
	},
	// Get industry type options
	industryTypes: async () => api.get<Array<{ value: string; label: string; color: string }>>('/map/industry-types'),
	// Get industry summaries
	industrySummaries: async () => api.get<{ summaries: Array<{ industry_type: string; count: number; has_location: number; color: string }> }>('/map/industry-summaries')
};
